// ═══════════════════════════════════════════════════════════════════════════════
// Client-side receipt image validation + WebP/JPEG compression
// ═══════════════════════════════════════════════════════════════════════════════

import { ALLOWED_IMAGE_TYPES, LEVEL1_DEFAULTS } from '@fdh/shared';

const MAX_EDGE_PRIMARY = 1600;
/** When canvas / decode fails (e.g. iPhone Safari camera PNG), retry with smaller targets. */
const MAX_EDGE_FALLBACKS: readonly number[] = [1600, 1280, 1024, 800, 640];

const isIosDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iP(hone|ad|od)/i.test(ua)) return true;
  // iPadOS 13+ "Request Desktop Website": MacIntel + touch
  const platform = navigator.platform ?? '';
  return platform === 'MacIntel' && navigator.maxTouchPoints > 1;
};

/** True iOS Safari (not Chrome/Firefox/Edge on iOS — those still use WebKit but behave closer to our Chrome path). */
const isIosSafariBrowser = (): boolean => {
  if (!isIosDevice()) return false;
  const ua = navigator.userAgent || '';
  return !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
};

export const createClientId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const guessMimeFromName = (name: string): string | null => {
  const lower = name.toLowerCase();
  const dot = lower.lastIndexOf('.');
  if (dot < 0) return null;
  const ext = lower.slice(dot);
  const map: Record<string, string> = {
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.webp': 'image/webp',
    '.heic': 'image/heic',
    '.heif': 'image/heif',
  };
  return map[ext] ?? null;
};

export const resolveClientImageMime = (file: File): string | null => {
  const from = file.type?.toLowerCase().trim();
  if (from && (ALLOWED_IMAGE_TYPES as readonly string[]).includes(from)) {
    return from === 'image/jpg' ? 'image/jpeg' : from;
  }
  return guessMimeFromName(file.name);
};

export type ReceiptFileValidationError = 'tooLarge' | 'badType';

export const validateReceiptFile = (file: File): ReceiptFileValidationError | null => {
  if (file.size > LEVEL1_DEFAULTS.MAX_FILE_SIZE_BYTES) {
    return 'tooLarge';
  }
  const mime = resolveClientImageMime(file);
  if (!mime) {
    return 'badType';
  }
  return null;
};

export type CompressedReceiptBlob = {
  blob: Blob;
  fileName: string;
  mime: string;
};

const baseNameFromFileName = (fileName: string): string =>
  fileName.replace(/\.[^.]+$/, '') || 'receipt';

const dataUrlToBlob = (dataUrl: string): Blob | null => {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return null;
  const header = dataUrl.slice(0, comma);
  const body = dataUrl.slice(comma + 1);
  const mimeMatch = /^data:([^;]+)/i.exec(header);
  const mime = mimeMatch?.[1]?.trim() ?? 'image/jpeg';
  try {
    const binary = atob(body);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
  } catch {
    return null;
  }
};

/**
 * iOS WebKit often passes null to toBlob even when the canvas is valid; toDataURL usually works.
 */
const canvasToBlobWithDataUrlFallback = async (
  canvas: HTMLCanvasElement,
  mime: string,
  quality: number
): Promise<Blob | null> => {
  const fromBlob = await new Promise<Blob | null>((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b), mime, quality);
    } catch {
      resolve(null);
    }
  });
  if (fromBlob && fromBlob.size > 0) {
    return fromBlob;
  }
  try {
    const dataUrl = canvas.toDataURL(mime, quality);
    if (!dataUrl || !dataUrl.startsWith('data:')) {
      return null;
    }
    return dataUrlToBlob(dataUrl);
  } catch {
    return null;
  }
};

const encodeCanvasToCompressedReceipt = async (
  canvas: HTMLCanvasElement,
  originalFileName: string
): Promise<CompressedReceiptBlob> => {
  const base = baseNameFromFileName(originalFileName);
  // iOS: canvas WebP encode is unreliable — try JPEG first; desktop: prefer WebP then JPEG.
  const order = isIosDevice()
    ? (['jpeg', 'webp'] as const)
    : (['webp', 'jpeg'] as const);

  for (const kind of order) {
    if (kind === 'webp') {
      const blob = await canvasToBlobWithDataUrlFallback(canvas, 'image/webp', 0.85);
      if (blob && blob.size > 0) {
        return { blob, fileName: `${base}.webp`, mime: 'image/webp' };
      }
    } else {
      const blob = await canvasToBlobWithDataUrlFallback(canvas, 'image/jpeg', 0.88);
      if (blob && blob.size > 0) {
        return { blob, fileName: `${base}.jpg`, mime: 'image/jpeg' };
      }
    }
  }
  throw new Error('compress');
};

const compressFromBitmap = async (
  bitmap: ImageBitmap,
  file: File,
  maxEdge: number
): Promise<CompressedReceiptBlob> => {
  const w = bitmap.width;
  const h = bitmap.height;
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const tw = Math.round(w * scale);
  const th = Math.round(h * scale);

  const canvas = document.createElement('canvas');
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('canvas');
  }
  ctx.drawImage(bitmap, 0, 0, tw, th);
  return encodeCanvasToCompressedReceipt(canvas, file.name);
};

const waitForImageNaturalSize = (img: HTMLImageElement): Promise<void> =>
  new Promise((resolve, reject) => {
    if (img.complete && img.naturalWidth > 0) {
      resolve();
      return;
    }
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('image load'));
  });

type LoadedImage = { img: HTMLImageElement; dispose: () => void };

/**
 * Do not revoke the object URL until after all drawImage / canvas steps — early revoke breaks WebKit.
 * decode() can reject on iOS while the image still loads via onload.
 */
const loadImageFromFileViaObjectUrl = async (file: File): Promise<LoadedImage> => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.src = url;

  if (typeof img.decode === 'function') {
    try {
      await img.decode();
    } catch {
      await waitForImageNaturalSize(img);
    }
  } else {
    await waitForImageNaturalSize(img);
  }

  if (img.naturalWidth === 0 || img.naturalHeight === 0) {
    URL.revokeObjectURL(url);
    throw new Error('zero size');
  }

  return {
    img,
    dispose: () => URL.revokeObjectURL(url),
  };
};

/**
 * iOS Safari: camera `File` + blob: URL often fails decode/draw; FileReader Data URL is more reliable.
 */
const loadImageFromFileViaDataUrl = async (file: File): Promise<LoadedImage> => {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const r = fr.result;
      if (typeof r === 'string') {
        resolve(r);
        return;
      }
      reject(new Error('read'));
    };
    fr.onerror = () => reject(new Error('read'));
    fr.readAsDataURL(file);
  });
  const img = new Image();
  img.src = dataUrl;

  if (typeof img.decode === 'function') {
    try {
      await img.decode();
    } catch {
      await waitForImageNaturalSize(img);
    }
  } else {
    await waitForImageNaturalSize(img);
  }

  if (img.naturalWidth === 0 || img.naturalHeight === 0) {
    throw new Error('zero size');
  }

  return {
    img,
    dispose: () => {
      img.removeAttribute('src');
      img.src = '';
    },
  };
};

const compressFromHtmlImage = async (
  file: File,
  img: HTMLImageElement,
  maxEdge: number
): Promise<CompressedReceiptBlob> => {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (w === 0 || h === 0) {
    throw new Error('zero size');
  }
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const tw = Math.round(w * scale);
  const th = Math.round(h * scale);

  const canvas = document.createElement('canvas');
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('canvas');
  }
  ctx.drawImage(img, 0, 0, tw, th);
  return encodeCanvasToCompressedReceipt(canvas, file.name);
};

const runImageElementCompress = async (
  file: File,
  loaders: Array<() => Promise<LoadedImage>>
): Promise<CompressedReceiptBlob> => {
  let lastError: unknown;
  for (const load of loaders) {
    try {
      const { img, dispose } = await load();
      try {
        for (const edge of MAX_EDGE_FALLBACKS) {
          try {
            return await compressFromHtmlImage(file, img, edge);
          } catch {
            // Canvas dimension limits or encode failure — try a smaller max edge.
          }
        }
      } finally {
        dispose();
      }
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('image element compress');
};

const tryCompressViaImageElement = async (file: File): Promise<CompressedReceiptBlob> => {
  const loaders = isIosSafariBrowser()
    ? [() => loadImageFromFileViaDataUrl(file), () => loadImageFromFileViaObjectUrl(file)]
    : [() => loadImageFromFileViaObjectUrl(file), () => loadImageFromFileViaDataUrl(file)];
  return runImageElementCompress(file, loaders);
};

const createBitmapFromFile = async (file: File): Promise<ImageBitmap> => {
  // Safari + from-image has regressed camera captures; Chrome iOS is fine with default order elsewhere.
  if (isIosSafariBrowser()) {
    return createImageBitmap(file);
  }
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return createImageBitmap(file);
  }
};

/**
 * Resize (max edge) + WebP compression; falls back to JPEG if WebP is unavailable.
 * Uses createImageBitmap first; on failure (common on iPhone Safari camera PNG), falls back to
 * HTMLImageElement + decode() and progressively smaller max dimensions.
 */
export const compressImageFile = async (file: File): Promise<CompressedReceiptBlob> => {
  const mime = resolveClientImageMime(file);
  if (!mime) {
    throw new Error('badType');
  }

  const tryBitmap = async (): Promise<CompressedReceiptBlob> => {
    const bitmap = await createBitmapFromFile(file);
    try {
      return await compressFromBitmap(bitmap, file, MAX_EDGE_PRIMARY);
    } finally {
      bitmap.close();
    }
  };

  // iOS Safari: bitmap/from-image and blob: URLs are flaky for camera files — decode via Data URL first.
  if (isIosSafariBrowser()) {
    try {
      return await tryCompressViaImageElement(file);
    } catch {
      // Fall through to createImageBitmap (plain, no from-image).
    }
    return tryBitmap();
  }

  try {
    return await tryBitmap();
  } catch {
    // Fall through to Image()-based path.
  }

  return tryCompressViaImageElement(file);
};
